import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";

import { useApi } from "@/api";
import { PageHeader } from "@/components/manage/PageHeader";
import { SpaceMembersSection } from "@/components/manage/SpaceMembersSection";
import { StatStrip } from "@/components/manage/StatStrip";
import { useActiveOrgId } from "@/components/manage/org-context";

export const Route = createFileRoute("/manage/projects/$id")({
  component: ProjectDetailPage,
});

interface SpaceSummary {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  workspace_id: string;
  member_count: number;
  updated_at: string;
}

function ProjectDetailPage() {
  const api = useApi();
  const orgId = useActiveOrgId();
  const { id } = useParams({ from: "/manage/projects/$id" });

  const project = useQuery({
    queryKey: ["admin-projects", orgId],
    queryFn: () => api.request<SpaceSummary[]>(`/admin/orgs/${orgId}/projects`),
    select: (rows) => rows.find((r) => r.id === id) ?? null,
  });

  if (project.isLoading) {
    return (
      <div className="mx-auto max-w-[960px] px-10 py-10">
        <Skeleton className="h-10 w-60" />
        <Skeleton className="mt-8 h-40 w-full" />
      </div>
    );
  }

  const p = project.data;
  if (!p) {
    return (
      <div className="mx-auto max-w-[960px] px-10 py-10">
        <Link
          to="/manage/projects"
          className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-3" /> All projects
        </Link>
        <p className="text-sm text-muted-foreground">Project not found.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[960px] px-10 py-10">
      <Link
        to="/manage/projects"
        className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-3" /> All projects
      </Link>

      <PageHeader
        eyebrow="Project"
        title={p.name}
        tabTitle={[p.name, "Projects"]}
        meta={
          <>
            <code className="rounded bg-[var(--color-surface-soft)] px-1.5 py-0.5 font-mono text-[11px]">
              /{p.slug}
            </code>
            <span>· Updated {new Date(p.updated_at).toLocaleDateString()}</span>
          </>
        }
      />

      <StatStrip
        items={[
          { label: "Members", value: p.member_count, hint: "have access" },
          {
            label: "Slug",
            value: <span className="font-mono text-base">/{p.slug}</span>,
            hint: "url segment",
          },
          { label: "Scope", value: "people", hint: "no datasets" },
        ]}
      />

      <SpaceMembersSection spaceId={id} orgId={orgId} subjectLabel="project" />
    </div>
  );
}
